import React from "react";
import PropTypes from "prop-types";
import CountUp from "react-countup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinusCircle } from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import { formatGoalTitle, asMoneyString, formatCurrencySymbol } from "../../utils/formatters";

export function getColor(percent) {
  if (typeof percent == "string") {
    return "hsl(213, 80%,  42%)";
  }

  const p = (percent * 100) / 100;
  const luma = 58;
  const increase = 1.42 + p - 0.82;

  if (p > 1) {
    return "rgb(18, 85, 166)";
  }

  return "hsl(213, 80%, " + luma / increase + "%)";
}

export function getPercent(value) {
  return Math.floor(value * 100).toString() + "%";
}

export function getValue(propValue) {
  if (typeof propValue == "string") {
    return propValue;
  }
  return propValue <= 0 ? 0 : propValue;
}

export function calculateWidth(val) {
  let newVal = val.split("%");
  if (newVal[0] < 100) {
    return newVal[0] + "%";
  }

  if (val === Infinity + "%" || newVal[0] === "NaN") {
    return "0%";
  }

  return "100%";
}

class ProjectedBar extends React.Component {
  static defaultProps = {
    isAnimated: true,
    isPercent: true
  };

  constructor(props) {
    super(props);
    this.state = {
      value: "0%",
      propValue: 0,
      projectedValue: "0%",
      propProjectedValue: 0
    };
  }

  animateBar() {
    const propValue = getValue(this.props.value);
    const propProjectedValue = getValue(this.props.projectedValue);

    // Need to set timeout so we can see the animation
    setTimeout(() => {
      this.setState({
        propValue,
        propProjectedValue,
        value: getPercent(propValue),
        projectedValue: getPercent(propProjectedValue)
      });
    }, 1);
  }

  componentDidMount() {
    this.animateBar();
  }

  componentDidUpdate() {
    if (
      this.state.propValue === getValue(this.props.value) &&
      this.state.propProjectedValue === getValue(this.props.projectedValue)
    ) {
      return;
    }

    this.animateBar();
  }

  getBarValue(value) {
    return this.props.isPercent ? (isFinite(value) ? (value * 100).toFixed() : 0) : value;
  }

  getWidth(value) {
    return this.props.fillWidth ? "100%" : calculateWidth(value);
  }

  getDataTip(label, value) {
    const symbol = formatCurrencySymbol(this.props.metricTitle, this.props.currencySymbol);

    return this.props.defaultTooltip
      ? `${label}: ${symbol ? symbol : ""}${asMoneyString(value)}`
      : this.props.tooltip
      ? this.props.tooltip
      : null;
  }

  getData() {
    return {
      value: this.getBarValue(this.state.propValue),
      projected: this.getBarValue(this.state.propProjectedValue),
      symbol: this.props.isPercent ? "%" : ""
    };
  }

  renderStackedBar() {
    const { value, projected, symbol } = this.getData();

    return (
      <div className="bar" style={styles.barStacked}>
        <div className="bar-wrapper" style={styles.barWrapperStacked}>
          <div
            className="bar-value"
            style={{
              ...styles.barValue,
              width: this.getWidth(this.state.value),
              backgroundColor: getColor(this.state.propValue),
              transition: this.props.isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: this.state.propValue < this.state.propProjectedValue ? 2 : 1
            }}
            data-testid="stacked-bar-value"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitleStacked, color: "white" }}
              data-place={"left"}
              data-tip={this.getDataTip("Current", this.props.currentVal)}
              data-testid="stacked-value-title"
            >
              {this.state.propValue * 100 === Infinity ? (
                <span>0%</span>
              ) : (
                <span>
                  {this.props.isAnimated ? <CountUp end={value} duration={2.5} /> : value}
                  {symbol}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="bar-wrapper" style={styles.barWrapperStacked}>
          <div
            className="bar-projected"
            style={{
              ...styles.barProjected,
              width: this.getWidth(this.state.projectedValue),
              backgroundColor: "#fd6847",
              transition: this.props.isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: this.state.propProjectedValue < this.state.propValue ? 2 : 1
            }}
            data-testid="stacked-bar-projected"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitleStacked, ...styles.valueProjected }}
              data-place={"right"}
              data-tip={this.getDataTip("Projected", this.props.currentValPacing)}
              data-testid="stacked-value-title-2"
            >
              <span>
                {this.props.isAnimated ? <CountUp end={projected} duration={2.5} /> : projected}
                {symbol}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  renderWithoutStackedBar() {
    const { value, projected, symbol } = this.getData();

    return (
      <div className="bar" style={styles.bar}>
        <div className="bar-wrapper" style={styles.barWrapper}>
          <div
            className="bar-value"
            style={{
              ...styles.barValue,
              width: this.getWidth(this.state.value),
              backgroundColor: getColor(this.state.propValue),
              transition: this.props.isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: this.state.propValue < this.state.propProjectedValue ? 2 : 1
            }}
            data-testid="bar-value"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitle, color: "white" }}
              data-place={"left"}
              data-tip={this.getDataTip("Current", this.props.currentVal)}
              data-testid="value-title"
            >
              <span>
                {this.props.isAnimated ? <CountUp end={value} duration={2.5} /> : value}
                {symbol}
              </span>
            </p>
          </div>
          <div
            className="bar-projected"
            style={{
              ...styles.barProjected,
              width: this.getWidth(this.state.projectedValue),
              backgroundColor: "#fd6847",
              transition: this.props.isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: this.state.propProjectedValue < this.state.propValue ? 2 : 1
            }}
            data-testid="bar-projected"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitle, ...styles.valueProjected }}
              data-place={"right"}
              data-tip={this.getDataTip("Projected", this.props.currentValPacing)}
              data-testid="value-title-2"
            >
              <span>
                {this.props.isAnimated ? <CountUp end={projected} duration={2.5} /> : projected}
                {symbol}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div style={{ width: "450xpx" }}>
        {this.props.defaultTooltip || this.props.tooltip ? <ReactTooltip /> : null}

        <p className="location-name" style={styles.locationName} data-testid="location-name">
          {this.props.locationName}
        </p>
        <p className="metric-title" style={styles.metricTitle} data-testid="metric-title">
          {formatGoalTitle(this.props.metricTitle)}
        </p>

        {this.props.isStacked ? this.renderStackedBar() : this.renderWithoutStackedBar()}

        {!this.props.hideMinus && (
          <div className="minus" style={styles.minus} data-testid="minus">
            <FontAwesomeIcon
              className="minus-icon"
              icon={faMinusCircle}
              style={{ color: "#8ac934" }}
              onClick={this.props.onMinusClick}
            />
          </div>
        )}
      </div>
    );
  }
}

const styles = {
  metricTitle: {
    color: "#7f838e",
    paddingBottom: "5px",
    display: "inline-flex",
    margin: 0,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: "12px"
  },
  locationName: {
    color: "rgb(135, 145, 159)",
    lineHeight: "12px",
    fontSize: "12px",
    margin: 0,
    position: "relative",
    top: "5px",
    width: "350px"
  },
  bar: {
    width: "97%",
    float: "left"
  },
  barStacked: {
    width: "97%",
    float: "left",
    position: "relative",
    top: "-8px"
  },
  minus: {
    width: "3%",
    paddingLeft: "3px",
    float: "left",
    cursor: "pointer",
    right: "-17px",
    position: "absolute"
  },
  barWrapper: {
    width: "100%",
    height: "23px",
    overflow: "hidden",
    position: "relative",
    marginBottom: "10px"
  },
  barWrapperStacked: {
    width: "100%",
    height: "18px",
    overflow: "hidden",
    position: "relative",
    marginBottom: "1px"
  },
  barValue: {
    width: "0%"
  },
  barProjected: {
    width: "0%"
  },
  valueTitle: {
    textAlign: "right",
    fontWeight: 600,
    paddingRight: "5px",
    fontSize: "12px"
  },
  valueTitleStacked: {
    textAlign: "right",
    fontWeight: 600,
    paddingRight: "5px",
    fontSize: "12px",
    position: "relative",
    top: "-2px"
  },
  valueProjected: {
    opacity: 1,
    color: "white"
  }
};

ProjectedBar.propTypes = {
  value: PropTypes.number,
  projectedValue: PropTypes.number,
  locationName: PropTypes.string,
  metricTitle: PropTypes.string,
  onMinusClick: PropTypes.func.isRequired,
  hideMinus: PropTypes.bool,
  currentVal: PropTypes.number,
  currentValPacing: PropTypes.number,
  currencySymbol: PropTypes.any,
  isStacked: PropTypes.bool,
  isAnimated: PropTypes.bool,
  tooltip: PropTypes.string,
  defaultTooltip: PropTypes.bool,
  isPercent: PropTypes.bool,
  fillWidth: PropTypes.bool
};

export default ProjectedBar;
